import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, IconButton, CircularProgress, Alert, Button, GlobalStyles, Chip, Stack } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { doc, getDoc, collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { useVideoTracker } from '../hooks/useVideoTracker';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useTranslation } from 'react-i18next';
import VideoLibrary from '../components/VideoLibrary';
import NextVideoSuggestion from '../components/NextVideoSuggestion';
import { getUserAllVideoProgress } from '../utils/videoUtils';
import { addToFavorites, removeFromFavorites, getUserFavoriteVideoIds } from '../services/favoriteService';


interface Video {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  thumbnailUrl: string;
  isIntro: boolean;
  requiredSubscription: boolean;
  module: number;
  category?: string;
  difficulty?: string;
  duration?: number;
  schede?: string; // Added schede field
}

const VideoPlayerPage: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  const [video, setVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextVideoSuggestionOpen, setNextVideoSuggestionOpen] = useState(false);
  const [nextVideo, setNextVideo] = useState<Video | null>(null);
  const [prevVideo, setPrevVideo] = useState<Video | null>(null);
  const [userProgress, setUserProgress] = useState<{ [videoId: string]: any }>({});
  const [userModules, setUserModules] = useState<number[]>([]);
  const [favoriteVideoIds, setFavoriteVideoIds] = useState<Set<string>>(new Set());
  const { t } = useTranslation();

  // Helper function to format time with leading zeros
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if user is admin or trainer
  const isAdminOrTrainer = role === 'admin' || role === 'trainer';

  // Use video tracker hook - only when video is loaded and user is available
  const { videoRef, loading: trackerLoading, progress } = useVideoTracker({
    videoId: video?.id || '',
    userId: user?.uid || '',
    completionThreshold: 0.95 // Mark as completed at 95%
  });

  // Load user progress and modules
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.uid) return;
      
      try {
        // Load user progress
        const progressData = await getUserAllVideoProgress(user.uid);
        const progressMap: { [videoId: string]: any } = {};
        progressData.forEach(progress => {
          let percentWatched = 0;
          if (progress.duration && progress.duration > 0 && progress.lastPosition >= 0) {
            percentWatched = Math.round((progress.lastPosition / progress.duration) * 100);
          }
          progressMap[progress.videoId] = {
            percentWatched: percentWatched,
            completed: progress.completed || false,
            hasStarted: progress.hasStarted || false
          };
        });
        setUserProgress(progressMap);

        // Load user modules (simplified - you might want to get this from user profile)
        const modules = [1, 2, 3, 4, 5]; // Default modules - adjust based on your logic
        setUserModules(modules);
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();
  }, [user?.uid]);

  // Sync real-time progress to userProgress for current video
  useEffect(() => {
    if (video?.id && progress.duration > 0) {
      const percentWatched = Math.round((progress.lastPosition / progress.duration) * 100);
      setUserProgress(prev => ({
        ...prev,
        [video.id]: {
          percentWatched: percentWatched,
          completed: progress.completed || false,
          hasStarted: progress.hasStarted !== false || progress.lastPosition > 0
        }
      }));
    }
  }, [video?.id, progress.lastPosition, progress.duration, progress.completed, progress.hasStarted]);

  // Find next video when current video is completed
  useEffect(() => {
    const findNextVideo = async () => {
      const percentWatched = progress.duration > 0 ? Math.round((progress.lastPosition / progress.duration) * 100) : 0;
      if (!video || !user?.uid) return;
      if (nextVideoSuggestionOpen) return; // avoid reopening repeatedly
      if (!(progress.completed || percentWatched >= 95)) return;

      try {
        // Get all videos in the same module
        const q = query(
          collection(db, 'videos'),
          where('requiredSubscription', '==', true),
          where('isIntro', '==', false),
          where('module', '==', video.module),
          orderBy('moduleOrder', 'asc')
        );
        const snapshot = await getDocs(q);
        const moduleVideos: Video[] = [];
        snapshot.forEach((doc) => {
          moduleVideos.push({ id: doc.id, ...doc.data() } as Video);
        });

        // Find prev/next within module
        const currentIndex = moduleVideos.findIndex(v => v.id === video.id);
        if (currentIndex > 0) {
          setPrevVideo(moduleVideos[currentIndex - 1]);
        } else {
          // Try previous module last video
          const prevModuleQ = query(
            collection(db, 'videos'),
            where('requiredSubscription', '==', true),
            where('isIntro', '==', false),
            where('module', '==', video.module - 1),
            orderBy('moduleOrder', 'asc')
          );
          const prevSnap = await getDocs(prevModuleQ);
          if (!prevSnap.empty) {
            const prevList = prevSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as Video[];
            setPrevVideo(prevList[prevList.length - 1]);
          } else {
            setPrevVideo(null);
          }
        }

        if (currentIndex < moduleVideos.length - 1) {
          setNextVideo(moduleVideos[currentIndex + 1]);
          setNextVideoSuggestionOpen(true);
        } else {
          // If this is the last video in module, suggest first video of next module
          const nextModuleQ = query(
            collection(db, 'videos'),
            where('requiredSubscription', '==', true),
            where('isIntro', '==', false),
            where('module', '==', video.module + 1),
            orderBy('moduleOrder', 'asc')
          );
          const nextModuleSnapshot = await getDocs(nextModuleQ);
          if (!nextModuleSnapshot.empty) {
            const nextModuleVideo = nextModuleSnapshot.docs[0].data() as Video;
            setNextVideo({ ...nextModuleVideo, id: nextModuleSnapshot.docs[0].id });
            setNextVideoSuggestionOpen(true);
          }
        }
      } catch (error) {
        console.error('Error finding next video:', error);
      }
    };

    findNextVideo();
  }, [video?.id, progress.lastPosition, progress.duration, progress.completed, user?.uid, nextVideoSuggestionOpen]);

  const handleVideoSelect = (selectedVideoId: string) => {
    navigate(`/video/${selectedVideoId}`);
  };

  const handlePlayNext = (nextVideoId: string) => {
    navigate(`/video/${nextVideoId}`);
  };
  const handlePlayPrev = (prevVideoId: string) => {
    navigate(`/video/${prevVideoId}`);
  };
  
  // Debug logging for video player interaction issues
  console.log('VideoPlayer Debug:', { 
    user: user?.email, 
    role, 
    isAdminOrTrainer, 
    loading: loading,
    trackerLoading: trackerLoading,
    authLoading: authLoading,
    videoId: videoId,
    video: video?.title
  });

  useEffect(() => {
    const fetchVideo = async () => {
      if (!videoId) {
        setError(t('videoPlayer.noVideoIdProvided'));
        setLoading(false);
        return;
      }

      // Wait for auth to be fully loaded before checking permissions
      if (authLoading) {
        console.log('Auth still loading, waiting...');
        return;
      }

      try {
        const videoDoc = await getDoc(doc(db, 'videos', videoId));
        if (!videoDoc.exists()) {
          setError(t('videoPlayer.videoNotFound'));
          setLoading(false);
          return;
        }

        const videoData = videoDoc.data();
        const videoObj = { id: videoDoc.id, ...videoData } as Video;
        setVideo(videoObj);

        console.log('Checking access for video:', videoObj.title, 'requiredSubscription:', videoObj.requiredSubscription, 'module:', videoObj.module);

        // Check access permissions
        if (videoObj.requiredSubscription) {
          console.log('Video requires subscription, checking role:', role, 'isAdminOrTrainer:', isAdminOrTrainer);
          
          // Admins and trainers always have access
          if (isAdminOrTrainer) {
            console.log('Admin/Trainer access granted');
            // Allow access - no further checks needed
          } else {
            console.log('Regular user, checking subscription...');
            // For regular users, check if they have access to this module
            if (user) {
              const userDoc = await getDoc(doc(db, 'users', user.uid));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const userModules = userData.subscription?.unlockedModules || [];
                const hasActiveSub = !!userData.subscription?.active;
                
                console.log('User subscription check:', { hasActiveSub, userModules, requiredModule: videoObj.module });
                
                if (!hasActiveSub || !userModules.includes(videoObj.module)) {
                  setError(t('videoPlayer.thisVideoRequiresSubscription'));
                  setLoading(false);
                  return;
                }
              } else {
                // User document doesn't exist, require subscription
                setError(t('videoPlayer.thisVideoRequiresSubscription'));
                setLoading(false);
                return;
              }
            } else {
              // No user logged in, require subscription
              setError(t('videoPlayer.thisVideoRequiresSubscription'));
              setLoading(false);
              return;
            }
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching video:', err);
        setError(t('videoPlayer.failedToLoadVideo'));
        setLoading(false);
      }
    };

    fetchVideo();
  }, [videoId, user, isAdminOrTrainer, authLoading, t]);

  // Debug video module for VideoLibrary
  useEffect(() => {
    console.log('VideoPlayer: Current video module for VideoLibrary:', video?.module);
  }, [video?.module]);

  // Load user favorites
  useEffect(() => {
    const loadFavorites = async () => {
      if (user?.uid) {
        try {
          const favoriteIds = await getUserFavoriteVideoIds(user.uid);
          setFavoriteVideoIds(favoriteIds);
        } catch (error) {
          console.error('Error loading favorites:', error);
        }
      }
    };

    loadFavorites();
  }, [user?.uid]);

  const handleBack = () => {
    navigate('/dashboard');
  };

  // Favorite functions
  const toggleFavorite = async (event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!user?.uid || !video) return;
    
    try {
      const isFavorited = favoriteVideoIds.has(video.id);
      
      if (isFavorited) {
        await removeFromFavorites(user.uid, video.id);
        setFavoriteVideoIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(video.id);
          return newSet;
        });
      } else {
        await addToFavorites(user.uid, video.id, {
          title: video.title,
          module: video.module,
          category: video.category
        });
        setFavoriteVideoIds(prev => new Set([...prev, video.id]));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  if (loading || authLoading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f5f5f5', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#E53935', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('videoPlayer.loadingYourVideo')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f5f5f5',
        background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <Paper elevation={8} sx={{ 
          p: 4, 
          maxWidth: 600, 
          textAlign: 'center',
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          border: '1px solid #e0e0e0'
        }}>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
          <IconButton 
            onClick={handleBack} 
            sx={{ 
              mt: 2,
              bgcolor: '#E53935',
              color: 'white',
              '&:hover': { bgcolor: '#C62828' }
            }}
          >
            <ArrowBackIcon /> {t('videoPlayer.backToDashboard')}
          </IconButton>
        </Paper>
      </Box>
    );
  }

  if (!video) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f5f5f5',
        background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <Paper elevation={8} sx={{ 
          p: 4, 
          maxWidth: 600, 
          textAlign: 'center',
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          border: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" sx={{ mb: 3, color: '#E53935' }}>{t('videoPlayer.videoNotFound')}</Typography>
          <IconButton 
            onClick={handleBack}
            sx={{ 
              bgcolor: '#E53935',
              color: 'white',
              '&:hover': { bgcolor: '#C62828' }
            }}
          >
            <ArrowBackIcon /> {t('videoPlayer.backToDashboard')}
          </IconButton>
        </Paper>
      </Box>
    );
  }

  const percentWatched = progress.duration > 0 ? Math.round((progress.lastPosition / progress.duration) * 100) : 0;

  return (
    <>
      <GlobalStyles
        styles={{
          '@keyframes shimmer': {
            '0%': {
              transform: 'translateX(-100%)',
            },
            '100%': {
              transform: 'translateX(100%)',
            },
          },
          '@keyframes borderGlow': {
            '0%, 100%': {
              opacity: 0.3,
            },
            '50%': {
              opacity: 0.8,
            },
          },
        }}
      />
      <Box sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a1f 50%, #0a0a0f 100%)',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
        position: 'relative',
        overflowX: 'hidden',
        bgcolor: '#0a0a0f',
        '&::before': {
          content: '""',
          position: { xs: 'absolute', md: 'fixed' },
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/martinbg3.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'scroll',
          opacity: 0.15,
          zIndex: 0,
          pointerEvents: 'none',
          width: '100%',
          height: '100%'
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, py: { xs: 3, md: 5 }, minHeight: '100vh', bgcolor: 'transparent' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, width: '100%', boxSizing: 'border-box' }}>
            {/* Minimal Header with Navigation */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: { xs: 3, md: 5 }
            }}>
              <IconButton 
                onClick={handleBack} 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  bgcolor: 'rgba(227, 28, 37, 0.15)',
                  border: '1px solid rgba(227, 28, 37, 0.3)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { 
                    bgcolor: 'rgba(227, 28, 37, 0.25)',
                    borderColor: 'rgba(227, 28, 37, 0.5)',
                    transform: 'translateX(-2px)'
                  },
                  transition: 'all 0.2s ease',
                  borderRadius: 2
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              
              <IconButton 
                onClick={toggleFavorite}
                sx={{ 
                  color: favoriteVideoIds.has(video?.id || '') ? '#FFD700' : 'rgba(255, 255, 255, 0.7)',
                  bgcolor: favoriteVideoIds.has(video?.id || '') ? 'rgba(255, 215, 0, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { 
                    bgcolor: favoriteVideoIds.has(video?.id || '') ? 'rgba(255, 215, 0, 0.25)' : 'rgba(255, 255, 255, 0.12)',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease',
                  borderRadius: 2
                }}
              >
                {favoriteVideoIds.has(video?.id || '') ? (
                  <StarIcon />
                ) : (
                  <StarBorderIcon />
                )}
              </IconButton>
            </Box>

            {/* Hero Video Section */}
            <Box sx={{ mb: { xs: 4, md: 6 } }}>
              <Box sx={{ 
                position: 'relative',
                borderRadius: { xs: 2, md: 3 },
                overflow: 'hidden',
                border: '1px solid rgba(227, 28, 37, 0.2)',
                background: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }}>

                {/* Video Player */}
                <Box sx={{ 
                  position: 'relative',
                  pointerEvents: 'auto',
                  cursor: 'default',
                  background: '#000'
                }}>
                  {trackerLoading && (
                    <Box sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      bgcolor: 'rgba(0,0,0,0.8)',
                      borderRadius: 2,
                      p: 3,
                      backdropFilter: 'blur(10px)'
                    }}>
                      <CircularProgress size={50} sx={{ color: '#E31C25' }} />
                    </Box>
                  )}
                  <video
                    ref={videoRef}
                    src={video.videoUrl}
                    controls
                    preload="metadata"
                    poster={video.thumbnailUrl}
                    style={{ 
                      width: '100%', 
                      height: 'auto',
                      minHeight: '250px',
                      maxHeight: '70vh',
                      display: 'block',
                      backgroundColor: '#000',
                      pointerEvents: 'auto',
                      cursor: 'pointer'
                    }}
                  />
                </Box>

                {/* Video Info Overlay */}
                <Box sx={{
                  background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.95) 30%)',
                  p: { xs: 3, md: 4 },
                  position: 'relative'
                }}>
                  {/* Title & Meta */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 300,
                      color: '#ffffff',
                      fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                      lineHeight: 1.2,
                      mb: 2,
                      letterSpacing: '-0.02em'
                    }}>
                      {video.title}
                    </Typography>
                    
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                      {video.category && (
                        <Chip 
                          label={video.category} 
                          size="small" 
                          sx={{ 
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            fontSize: '0.75rem',
                            height: '24px',
                            fontWeight: 500
                          }}
                        />
                      )}
                      <Chip 
                        label={t('dashboard.moduleLabel', { number: video.module })} 
                        size="small" 
                        sx={{ 
                          bgcolor: 'rgba(33, 150, 243, 0.15)',
                          color: '#2196F3',
                          border: '1px solid rgba(33, 150, 243, 0.3)',
                          fontSize: '0.75rem',
                          height: '24px',
                          fontWeight: 600
                        }}
                      />
                      {video.difficulty && (
                        <Chip 
                          label={video.difficulty.charAt(0).toUpperCase() + video.difficulty.slice(1).toLowerCase()} 
                          size="small" 
                          sx={{ 
                            bgcolor: (() => {
                              const d = video.difficulty.toLowerCase();
                              if (d === 'beginner') return 'rgba(76, 175, 80, 0.15)';
                              if (d === 'intermediate') return 'rgba(255, 193, 7, 0.15)';
                              if (d === 'advanced' || d === 'hard') return 'rgba(244, 67, 54, 0.15)';
                              return 'rgba(255, 255, 255, 0.15)';
                            })(),
                            color: (() => {
                              const d = video.difficulty.toLowerCase();
                              if (d === 'beginner') return '#4CAF50';
                              if (d === 'intermediate') return '#FFC107';
                              if (d === 'advanced' || d === 'hard') return '#F44336';
                              return '#FFFFFF';
                            })(),
                            border: (() => {
                              const d = video.difficulty.toLowerCase();
                              if (d === 'beginner') return '1px solid rgba(76, 175, 80, 0.3)';
                              if (d === 'intermediate') return '1px solid rgba(255, 193, 7, 0.3)';
                              if (d === 'advanced' || d === 'hard') return '1px solid rgba(244, 67, 54, 0.3)';
                              return '1px solid rgba(255, 255, 255, 0.2)';
                            })(),
                            fontSize: '0.75rem',
                            height: '24px',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Stack>

                    {/* Description */}
                    <Typography variant="body1" sx={{ 
                      color: 'rgba(255, 255, 255, 0.75)',
                      lineHeight: 1.7,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      maxWidth: '800px'
                    }}>
                      {video.description}
                    </Typography>
                  </Box>

                  {/* Progress Bar */}
                  {progress.duration > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 1
                      }}>
                        <Typography variant="body2" sx={{ 
                          color: '#E31C25', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          {percentWatched}% {t('videoPlayer.watched')}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: '0.875rem'
                        }}>
                          {formatTime(progress.lastPosition)} / {formatTime(progress.duration)}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: '100%', 
                        height: 6, 
                        background: 'rgba(255, 255, 255, 0.1)', 
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.3)'
                      }}>
                        <Box sx={{ 
                          width: `${percentWatched}%`, 
                          height: '100%', 
                          background: 'linear-gradient(90deg, #E31C25, #ff4757)',
                          transition: 'width 0.3s ease',
                          borderRadius: 3,
                          boxShadow: '0 0 10px rgba(227, 28, 37, 0.5)'
                        }} />
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        mt: 1.5,
                        gap: 2
                      }}>
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.7rem'
                        }}>
                          {t('videoPlayer.views', { count: progress.viewCount })}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: progress.completed ? '#4CAF50' : 'rgba(255, 152, 0, 0.8)', 
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}>
                          {progress.completed ? t('common.completed') : t('common.inProgress')}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* PDF Link */}
                  {video.schede && (
                    <Button
                      variant="outlined"
                      href={video.schede}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        borderRadius: 2, 
                        fontWeight: 600,
                        borderColor: '#4CAF50',
                        color: '#4CAF50',
                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                        '&:hover': {
                          borderColor: '#45a049',
                          color: '#45a049',
                          bgcolor: 'rgba(76, 175, 80, 0.2)',
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.2s ease',
                        px: 3,
                        py: 1
                      }}
                    >
                      {t('common.viewPdf')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Content Grid */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: { xs: 4, md: 5 },
              mb: { xs: 4, md: 6 },
              alignItems: 'stretch'
            }}>
              {/* Main Content - Video Details */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ 
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: 3,
                  p: { xs: 2, md: 3 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <Typography variant="h6" sx={{ 
                    mb: 1.5,
                    color: '#ffffff',
                    fontWeight: 300,
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    letterSpacing: '-0.01em',
                    borderBottom: '1px solid rgba(227, 28, 37, 0.2)',
                    pb: 1
                  }}>
                    {t('videoPlayer.videoDetails')}
                  </Typography>
                  
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 2, md: 3 }}
                    sx={{ 
                      flexWrap: 'wrap',
                      alignItems: { xs: 'flex-start', sm: 'center' }
                    }}
                  >
                    {video.category && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 500, fontSize: '0.8rem' }}>
                          {t('videoPlayer.category')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 400, fontSize: '0.8rem' }}>
                          {video.category}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 500, fontSize: '0.8rem' }}>
                        {t('videoPlayer.module')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#2196F3', fontWeight: 600, fontSize: '0.8rem' }}>
                        {t('dashboard.moduleLabel', { number: video.module })}
                      </Typography>
                    </Box>
                    {video.difficulty && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 500, fontSize: '0.8rem' }}>
                          {t('videoPlayer.level')}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: (() => {
                            const d = video.difficulty.toLowerCase();
                            if (d === 'beginner') return '#4CAF50';
                            if (d === 'intermediate') return '#FFC107';
                            if (d === 'advanced' || d === 'hard') return '#F44336';
                            return '#FFFFFF';
                          })(), 
                          fontWeight: 600,
                          fontSize: '0.8rem'
                        }}>
                          {video.difficulty.charAt(0).toUpperCase() + video.difficulty.slice(1).toLowerCase()}
                        </Typography>
                      </Box>
                    )}
                    {video.duration && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 500, fontSize: '0.8rem' }}>
                          {t('videoPlayer.duration')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 400, fontSize: '0.8rem' }}>
                          {formatTime(video.duration)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Box>

              {/* Sidebar - Progress Stats */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ 
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: 3,
                  p: { xs: 2, md: 3 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <Typography variant="h6" sx={{ 
                    mb: 1.5,
                    color: '#ffffff',
                    fontWeight: 300,
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    letterSpacing: '-0.01em',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                    pb: 1
                  }}>
                    {t('videoPlayer.yourProgress')}
                  </Typography>
                  
                  {progress.duration > 0 ? (
                    <Stack 
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 1, sm: 2, md: 3 }}
                      sx={{ 
                        flexWrap: 'wrap',
                        alignItems: { xs: 'flex-start', sm: 'center' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {t('videoPlayer.watched')}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.8rem'
                        }}>
                          {percentWatched}%
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.65rem',
                          ml: 0.5
                        }}>
                          ({formatTime(progress.lastPosition)} / {formatTime(progress.duration)})
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {t('videoPlayer.views', { count: 0 }).replace('0', '')}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.8rem'
                        }}>
                          {progress.viewCount}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {t('videoPlayer.totalWatchtime')}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.8rem'
                        }}>
                          {Math.floor(progress.watchTime / 60)}m {Math.floor(progress.watchTime % 60).toString().padStart(2, '0')}s
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {t('videoPlayer.statusLabel')}
                        </Typography>
                        <Chip
                          label={progress.completed ? t('common.completed') : t('common.inProgress')}
                          size="small"
                          sx={{
                            bgcolor: progress.completed ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 152, 0, 0.15)',
                            color: progress.completed ? '#4CAF50' : '#FF9800',
                            border: `1px solid ${progress.completed ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: '22px'
                          }}
                        />
                      </Box>
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                      {t('videoPlayer.noProgressYet')}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Video Library Section */}
            <Box sx={{ mb: 4 }}>
              <VideoLibrary
                currentVideoId={video?.id || ''}
                onVideoSelect={handleVideoSelect}
                userProgress={userProgress}
                userModules={userModules}
                currentVideoModule={video?.module}
                {...(progress.duration > 0 ? {
                  currentProgress: {
                    lastPosition: progress.lastPosition,
                    duration: progress.duration,
                    percentWatched: Math.round((progress.lastPosition / progress.duration) * 100),
                    completed: progress.completed,
                    hasStarted: progress.hasStarted
                  }
                } : {})}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Next Video Suggestion Modal */}
      <NextVideoSuggestion
        open={nextVideoSuggestionOpen}
        onClose={() => setNextVideoSuggestionOpen(false)}
        onPlayNext={handlePlayNext}
        onPlayPrev={handlePlayPrev}
        nextVideo={nextVideo}
        prevVideo={prevVideo}
      />
    </>
  );
};

export default VideoPlayerPage; 